/**
 * The workshop's motion. The pointer is the visitor's gaze: it pans the painted room,
 * lights whatever prop it is near, and lifts the hammer as it nears the anvil.
 *
 * Imperative and per-frame on purpose: React renders the scene once and this writes
 * transforms and CSS variables straight to the DOM, so animating never re-renders.
 * Easing is scaled by frame time, so a 120 Hz screen moves at the same speed as 60 Hz.
 */

export interface Motion {
  /** Swing the hammer; `onImpact` fires as it lands (at once under reduced motion). */
  strike(): void;
  /** A board is up: hold the gaze still and lean the camera in. */
  setBoardOpen(open: boolean): void;
  /** Set the plaques under the title swaying, as they drop into view. */
  nudge(): void;
  stop(): void;
}

/** The room painting's own pixel grid, which particle positions are written in. */
const ROOM_W = 1672;
const ROOM_H = 941;

const LIFT_DEG = 26;
const RAISE_MS = 140;
const SWING_MS = 110;
const HOLD_MS = 90;

/** With no input this long, the loop stops: an idle, visible page shouldn't drain a battery. */
const IDLE_MS = 6000;
/** A visitor who hasn't found a prop by now is shown all three signs. */
const INTRO_MS = 4000;
/** A finger that moves less than this is a tap, not a look. */
const DRAG_PX = 8;
/** A released drag settles on the nearest prop within this fraction of the scene's width. */
const SNAP_REACH = 0.3;
/** The phone plaques' pendulum: gravity in px/ms², how hard a pan kicks them, how fast they settle. */
const SWING_G = 0.0025;
const SWING_KICK = 0.25;
const SWING_DECAY_MS = 400;
const SWING_MAX = 0.25;
/** Clear air between the phone's doors and a sign held below them, so a swing never touches it. */
const SIGN_GAP = 12;
/** A mouse brushing a plaque: rad/ms per px it was moving, and the most one pass can add. */
const BRUSH_PER_PX = 0.0001;
const BRUSH_MAX = 0.0003;

type Spot = 'forge' | 'rack' | 'chest';

interface Ember {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  r: number;
}

export function startMotion(root: HTMLElement, onImpact: () => void): Motion {
  const q = <T extends Element = HTMLElement>(sel: string) => {
    const el = root.querySelector<T>(sel);
    if (!el) throw new Error(`workshop: missing ${sel}`);
    return el;
  };
  const stage = q('.ws-stage');
  const layers = [...root.querySelectorAll<HTMLElement>('[data-depth]')];
  const gaze = q('.ws-gaze');
  const hearth = q('.ws-glow-hearth');
  const lamps = [...root.querySelectorAll<HTMLElement>('.ws-glow-lamp')];
  const anvil = q('.ws-anvil');
  const anvilHearth = q('.ws-anvil-hearth');
  const hammer = q('.ws-hammer');
  const hammerShadow = q('.ws-hammer-shadow');
  const props = [...root.querySelectorAll<HTMLElement>('.ws-prop')];
  const rackWarm = q('.ws-rack .ws-prop-warm');
  const canvas = q<HTMLCanvasElement>('.ws-fx');
  const ctx = canvas.getContext('2d');
  const signs = q('.ws-signs');
  const pins = [...root.querySelectorAll<HTMLElement>('.ws-sign-anchor')].map(
    (a) => [a, signs.querySelector<HTMLElement>(`[data-sign="${a.dataset.sign}"]`)] as const,
  );
  const spots = [...root.querySelectorAll<HTMLElement>('[data-spot]')];
  const quick = q('.ws-quick');
  // Angle in radians, positive with the plaque's foot to the right; `px`/`pv` track its pivot.
  const swings = [...quick.querySelectorAll<HTMLElement>('.ws-quick-btn')].map((el) => ({ el, a: 0, v: 0, px: NaN, pv: 0 }));

  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (v: number, lo = -1, hi = 1) => Math.max(lo, Math.min(hi, v));

  // Gaze target in -1..1 and the eased value that chases it; `eye` is in scene pixels.
  const target = { x: 0, y: 0 };
  const cur = { x: 0, y: 0 };
  const eye = { x: 0, y: 0 };
  const eyeCur = { x: 0, y: 0 };
  let drag: { x: number; y: number; gx: number; gy: number; moved: boolean } | null = null;
  let boardOpen = false;
  let zoom = 1;

  // Hammer: `lift` (0..1) follows how near the gaze is; a strike overrides it for a beat.
  let lift = 0;
  let ang = 0;
  let strikeAt = -1;
  let strikeFrom = 0;
  let impactDone = false;
  let lastTrickle = 0;

  const embers: Ember[] = [];
  const dust = Array.from({ length: 60 }, () => ({
    x: 1150 + Math.random() * 420,
    y: 250 + Math.random() * 560,
    v: 0.08 + Math.random() * 0.25,
    p: Math.random() * 6,
  }));

  // Until the visitor first finds a prop themselves, all three signs fade in after a beat.
  let explored = false;
  const intro = window.setTimeout(() => {
    if (!explored && !boardOpen) signs.dataset.intro = '';
  }, INTRO_MS);
  function explore() {
    explored = true;
    window.clearTimeout(intro);
    delete signs.dataset.intro;
  }

  function setActive(name: Spot | null) {
    for (const el of [stage, signs]) {
      if (name) el.dataset.active = name;
      else delete el.dataset.active;
    }
  }

  function lookAt(el: HTMLElement) {
    const r = el.getBoundingClientRect();
    const box = root.getBoundingClientRect();
    target.x = clamp(((r.left + r.width / 2 - box.left) / box.width) * 2 - 1);
    target.y = clamp(((r.top + r.height / 2 - box.top) / box.height) * 2 - 1);
  }

  /** How far the stage can pan each way, in pixels, at a gaze of 1. */
  function reach(box: DOMRect) {
    return {
      x: Math.max(0, (stage.offsetWidth - box.width) / 2) * 0.95,
      y: Math.max(0, (stage.offsetHeight - box.height) / 2) * 0.95,
    };
  }

  /** Ease the nearest prop to the middle of the screen, where touch's light sits, and show its sign. */
  function snap() {
    const box = root.getBoundingClientRect();
    const span = reach(box);
    // Measure from where the drag is heading, not from the eased camera trailing behind it.
    const cx = box.left + box.width / 2 + (target.x - cur.x) * span.x;
    const cy = box.top + box.height / 2 + (target.y - cur.y) * span.y;
    let best: { el: HTMLElement; dx: number; dy: number; d: number } | null = null;
    for (const el of spots) {
      const r = el.getBoundingClientRect();
      if (!r.width) continue;
      const dx = r.left + r.width / 2 - cx;
      const dy = r.top + r.height / 2 - cy;
      // Reach is judged side to side: a tall screen can barely pan up or down to meet a prop.
      const d = Math.abs(dx) / box.width;
      if (d < SNAP_REACH && (!best || d < best.d)) best = { el, dx, dy, d };
    }
    if (!best) return;
    if (span.x) target.x = clamp(target.x + best.dx / span.x);
    if (span.y) target.y = clamp(target.y + best.dy / span.y);
    explore();
    setActive(best.el.dataset.spot as Spot);
  }

  const onMove = (e: PointerEvent) => {
    wake();
    if (boardOpen) return;
    const box = root.getBoundingClientRect();
    if (e.pointerType === 'mouse') {
      target.x = clamp(((e.clientX - box.left) / box.width) * 2 - 1);
      target.y = clamp(((e.clientY - box.top) / box.height) * 2 - 1);
      eye.x = e.clientX - box.left;
      eye.y = e.clientY - box.top;
    } else if (drag) {
      if (!drag.moved && Math.hypot(e.clientX - drag.x, e.clientY - drag.y) > DRAG_PX) {
        drag.moved = true;
        setActive(null);
      }
      // A finger drags the room, so looking right means pulling it left.
      target.x = clamp(drag.gx - ((e.clientX - drag.x) / box.width) * 2.4);
      target.y = clamp(drag.gy - ((e.clientY - drag.y) / box.height) * 2.4);
    }
  };
  const onDown = (e: PointerEvent) => {
    wake();
    if (e.pointerType !== 'mouse') drag = { x: e.clientX, y: e.clientY, gx: target.x, gy: target.y, moved: false };
  };
  const onUp = () => {
    if (drag?.moved && !boardOpen) snap();
    drag = null;
  };
  // A lifted finger also "leaves", so only a mouse leaving recentres the room.
  const onLeave = (e: PointerEvent) => {
    if (e.pointerType === 'mouse') target.x = target.y = 0;
  };
  root.addEventListener('pointermove', onMove);
  root.addEventListener('pointerdown', onDown);
  window.addEventListener('pointerup', onUp);
  root.addEventListener('pointerleave', onLeave);

  const spotOff: (() => void)[] = [];
  for (const s of swings) {
    // Pushed the way the mouse was going; a pointer with no movement pushes away from where it came in.
    const brush = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || reduce) return;
      const r = s.el.getBoundingClientRect();
      const px = e.movementX || (e.clientX < r.left + r.width / 2 ? 4 : -4);
      s.v += clamp(px * BRUSH_PER_PX, -BRUSH_MAX, BRUSH_MAX);
      wake();
    };
    s.el.addEventListener('pointerenter', brush);
    spotOff.push(() => s.el.removeEventListener('pointerenter', brush));
  }

  for (const el of spots) {
    const name = el.dataset.spot as Spot;
    const enter = () => {
      explore();
      setActive(name);
    };
    // Touch fires leave as the finger lifts, which would put a tapped sign straight out.
    const leave = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') setActive(null);
    };
    const blur = () => setActive(null);
    // Only keyboard focus turns the head; focus handed back after a click doesn't.
    const focus = () => {
      if (el.matches(':focus-visible')) {
        wake();
        explore();
        setActive(name);
        lookAt(el);
      }
    };
    el.addEventListener('pointerenter', enter);
    el.addEventListener('pointerleave', leave);
    el.addEventListener('focus', focus);
    el.addEventListener('blur', blur);
    spotOff.push(() => {
      el.removeEventListener('pointerenter', enter);
      el.removeEventListener('pointerleave', leave);
      el.removeEventListener('focus', focus);
      el.removeEventListener('blur', blur);
    });
  }

  function sparks(count: number) {
    for (let i = 0; i < count; i++) {
      const s = document.createElement('span');
      const angle = -Math.PI * (0.05 + Math.random() * 0.9);
      const dist = 40 + Math.random() * (count > 3 ? 160 : 60);
      s.className = 'ws-spark';
      s.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
      s.style.setProperty('--dy', `${Math.sin(angle) * dist}px`);
      s.style.setProperty('--t', `${500 + Math.random() * 500}ms`);
      s.addEventListener('animationend', () => s.remove());
      anvil.appendChild(s);
    }
  }

  function impact() {
    anvil.classList.remove('is-struck');
    void anvil.offsetWidth; // restart the animation on rapid strikes
    anvil.classList.add('is-struck');
    sparks(28);
    // A beat to see the sparks before the board drops.
    window.setTimeout(onImpact, 140);
  }

  let scale = 1;
  let dpr = 1;
  function resize() {
    dpr = Math.min(devicePixelRatio || 1, 2);
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    scale = canvas.offsetWidth / ROOM_W;
    if (reduce) place(performance.now(), 16.7);
    else wake();
  }
  const ro = new ResizeObserver(resize);
  ro.observe(root);

  /** One frame of everything. `dt` is milliseconds since the last frame. */
  function place(t: number, dt: number) {
    // Exponential easing per 60 Hz frame, scaled to this frame's length.
    const k = (rate: number) => 1 - Math.pow(1 - rate, dt / 16.67);
    const box = root.getBoundingClientRect();
    const hot = stage.dataset.active === 'forge';

    if (!reduce) {
      cur.x += (target.x - cur.x) * k(0.06);
      cur.y += (target.y - cur.y) * k(0.06);
      eyeCur.x += (eye.x - eyeCur.x) * k(0.12);
      eyeCur.y += (eye.y - eyeCur.y) * k(0.12);
      zoom += ((boardOpen ? 1.1 : 1) - zoom) * k(0.07);
    }
    // A little breathing even when the hand is still.
    const still = reduce || boardOpen;
    const x = cur.x + (still ? 0 : Math.sin(t / 2300) * 0.035);
    const y = cur.y + (still ? 0 : Math.cos(t / 3100) * 0.03);
    const span = reach(box);
    const panX = -x * span.x;
    const panY = -y * span.y;
    stage.style.transform = `translate(calc(-50% + ${panX}px), calc(-50% + ${panY}px)) rotateY(${x * -1.4}deg) rotateX(${y}deg) scale(${zoom})`;
    for (const l of layers) {
      const d = Number(l.dataset.depth);
      l.style.transform = `translate3d(${-x * d}px, ${-y * d * 0.5}px, 0)`;
    }

    // The title is carried along the room, so a change in pan speed swings what hangs from it.
    // Side by side (phone), each plaque hangs from the title; stacked (desktop), each hangs
    // from the plaque above and rides its swing. Layout offsets ignore transforms, so the
    // geometry never feeds back on itself.
    if (!reduce && quick.offsetParent) {
      const chained = getComputedStyle(quick).flexDirection === 'column';
      let off = 0;
      let rise = 0;
      let above = 0;
      for (const s of swings) {
        const { el } = s;
        const chain = el.offsetTop - above;
        // The pivot is the chain's top, the weight mid-plaque.
        const len = chain + el.offsetHeight / 2;
        const px = off - panX;
        const pv = Number.isNaN(s.px) ? 0 : (px - s.px) / dt;
        s.v -= ((pv - s.pv) / len) * Math.cos(s.a) * SWING_KICK;
        s.px = px;
        s.pv = pv;
        s.v -= (SWING_G / len) * Math.sin(s.a) * dt;
        s.v *= Math.exp(-dt / SWING_DECAY_MS);
        s.a = clamp(s.a + s.v * dt, -SWING_MAX, SWING_MAX);
        el.style.rotate = `${(-s.a).toFixed(4)}rad`;
        el.style.translate = chained ? `${off.toFixed(2)}px ${(-rise).toFixed(2)}px` : '';
        if (chained) {
          const foot = chain + el.offsetHeight;
          off += foot * Math.sin(s.a);
          rise += foot * (1 - Math.cos(s.a));
          above = el.offsetTop + el.offsetHeight;
        }
      }
    } else {
      // Hidden: the next showing starts from rest, not from a jump across the gap.
      for (const s of swings) s.px = NaN;
    }

    // Touch has no hover, so its light sits in the middle of the screen.
    const touch = !eye.x && !eye.y;
    const lx = touch ? box.width / 2 : eyeCur.x;
    const ly = touch ? box.height / 2 : eyeCur.y;
    gaze.style.setProperty('--lx', `${lx}px`);
    gaze.style.setProperty('--ly', `${ly}px`);

    // The gaze lights the metal: the highlight tracks the eye, and each prop brightens
    // the nearer you look.
    const ar = anvil.getBoundingClientRect();
    anvil.style.setProperty('--hx', `${((lx + box.left - ar.left) / ar.width) * 100}%`);
    anvil.style.setProperty('--hy', `${((ly + box.top - ar.top) / ar.height) * 100}%`);
    /** How far the gaze is from a box's centre, as a fraction of the scene. */
    const away = (r: DOMRect) =>
      Math.hypot((lx + box.left - (r.left + r.width / 2)) / box.width, (ly + box.top - (r.top + r.height / 2)) / box.height);
    const anvilAway = away(ar);
    anvil.style.setProperty('--lit', (hot ? 1.05 : 0.72 + 0.25 * clamp(1 - anvilAway / 0.45, 0, 1)).toFixed(3));
    for (const el of props) {
      el.style.setProperty('--lit', (0.7 + 0.28 * clamp(1 - away(el.getBoundingClientRect()) / 0.4, 0, 1)).toFixed(3));
    }

    const flicker = reduce ? 0 : 0.14 * Math.sin(t / 90) + 0.09 * Math.sin(t / 37 + 1) + 0.05 * Math.sin(t / 13);
    hearth.style.opacity = String(0.8 + flicker);
    anvilHearth.style.opacity = String(0.75 + flicker * 1.5);
    rackWarm.style.opacity = (0.7 + flicker * 1.5).toFixed(3);
    lamps.forEach((l, i) => {
      l.style.opacity = String(0.75 + (reduce ? 0 : 0.12 * Math.sin(t / (70 + i * 23) + i) + 0.06 * Math.sin(t / 19 + i)));
    });

    if (!reduce) {
      const near = clamp(1 - (anvilAway - 0.05) / 0.28, 0, 1);
      lift += ((hot ? 1 : near) - lift) * k(0.08);
      const rest = lift * LIFT_DEG + Math.sin(t / 420) * 2 * lift;
      if (strikeAt >= 0) {
        const e = t - strikeAt;
        // A tap with the hammer down raises it first.
        const up = strikeFrom < 15 ? RAISE_MS : 0;
        const top = up ? LIFT_DEG + 6 : strikeFrom;
        if (e < up) {
          const u = e / up;
          ang = strikeFrom + (top - strikeFrom) * (1 - (1 - u) * (1 - u));
        } else if (e < up + SWING_MS) {
          const u = (e - up) / SWING_MS;
          ang = top * (1 - u * u);
        } else {
          // A beat on the anvil, then it eases back up. No bounce: a bounce lands twice.
          if (!impactDone) {
            impactDone = true;
            impact();
          }
          ang = 0;
          if (e > up + SWING_MS + HOLD_MS) strikeAt = -1;
        }
      } else {
        ang += (rest - ang) * k(0.18);
      }
      const raised = clamp(ang / LIFT_DEG, 0, 1.2);
      hammer.style.transform = `translateY(${-raised * ar.height * 0.05}px) rotate(${ang}deg)`;
      hammerShadow.style.opacity = (0.85 - raised * 0.55).toFixed(3);
      hammerShadow.style.filter = `blur(${(raised * 6).toFixed(1)}px)`;
      if (raised > 0.6 && t - lastTrickle > 420) {
        sparks(1);
        lastTrickle = t;
      }
    }

    // Phone: the doors hang side by side over the top of the room, and an anchor pinned to the
    // room's height can ride up behind them on a short screen. Hold each sign just below the
    // lowest door instead; it only moves down, toward its own prop.
    let floor = -Infinity;
    if (quick.offsetParent && getComputedStyle(quick).flexDirection !== 'column') {
      for (const s of swings) floor = Math.max(floor, s.el.getBoundingClientRect().bottom - box.top + SIGN_GAP);
    }
    for (const [a, sign] of pins) {
      if (!sign) continue;
      const r = a.getBoundingClientRect();
      sign.style.left = `${r.left - box.left}px`;
      // A sign hangs above its anchor (`translate(..., -100%)`), so its top edge is `top - height`.
      sign.style.top = `${Math.max(r.top - box.top, floor + sign.offsetHeight)}px`;
    }

    if (!ctx || reduce) return;
    ctx.setTransform(scale * dpr, 0, 0, scale * dpr, 0, 0);
    ctx.clearRect(0, 0, ROOM_W, ROOM_H);
    const f = dt / 16.67;
    for (let n = (0.35 + Math.random() - 0.5) * f; n > 0; n--) {
      embers.push({ x: 470 + Math.random() * 70, y: 540, vx: (Math.random() - 0.5) * 0.5, vy: -(0.6 + Math.random() * 1.1), life: 1, r: 1 + Math.random() * 1.6 });
    }
    ctx.globalCompositeOperation = 'lighter';
    for (let i = embers.length - 1; i >= 0; i--) {
      const e = embers[i];
      if (!e) continue;
      // Drawn back toward the hearth's mouth as it rises.
      e.vx += ((Math.random() - 0.5) * 0.1 + (505 - e.x) * 0.002) * f;
      e.x += e.vx * f;
      e.y += e.vy * f;
      e.life -= 0.012 * f;
      if (e.life <= 0 || e.y < 440) {
        embers.splice(i, 1);
        continue;
      }
      ctx.fillStyle = `hsla(${22 + e.life * 22}, 100%, ${55 + e.life * 15}%, ${e.life})`;
      ctx.beginPath();
      ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = 'rgba(180, 205, 255, 0.35)';
    for (const d of dust) {
      d.y -= d.v * f;
      d.p += 0.008 * f;
      if (d.y < 250) d.y = 810;
      ctx.beginPath();
      ctx.arc(d.x + Math.sin(d.p) * 14, d.y, 1.4, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  let frame = 0;
  let last = performance.now();
  let lastInput = last;
  function tick(t: number) {
    place(t, Math.min(t - last, 100));
    last = t;
    // Idle: hold the last frame. A strike in flight always finishes.
    if (t - lastInput > IDLE_MS && strikeAt < 0) frame = 0;
    else frame = requestAnimationFrame(tick);
  }
  function wake() {
    lastInput = performance.now();
    if (reduce || frame) return;
    last = lastInput;
    frame = requestAnimationFrame(tick);
  }
  // Under reduced motion nothing moves, so one placement (and one per resize) is all.
  if (reduce) place(performance.now(), 16.7);
  else frame = requestAnimationFrame(tick);

  return {
    strike() {
      if (reduce) {
        onImpact();
        return;
      }
      if (strikeAt < 0) {
        wake();
        strikeAt = performance.now();
        strikeFrom = ang;
        impactDone = false;
        // The swing runs on animation frames, which a hidden or throttled page may not
        // get; the click must still open the board.
        window.setTimeout(() => {
          if (impactDone) return;
          impactDone = true;
          strikeAt = -1;
          onImpact();
        }, RAISE_MS + SWING_MS + 400);
      }
    },
    setBoardOpen(open) {
      boardOpen = open;
      wake();
      if (open) {
        explore();
        target.x = target.y = 0;
        setActive(null);
      }
    },
    nudge() {
      if (reduce) return;
      swings.forEach((s, i) => (s.v += (i % 2 ? -1 : 1) * 0.0003));
      wake();
    },
    stop() {
      cancelAnimationFrame(frame);
      window.clearTimeout(intro);
      ro.disconnect();
      root.removeEventListener('pointermove', onMove);
      root.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', onUp);
      root.removeEventListener('pointerleave', onLeave);
      for (const off of spotOff) off();
    },
  };
}
